import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Screen } from '@/components/Screen'
import { Kicker } from '@/components/Kicker'
import { ConfidenceChip } from '@/components/ConfidenceChip'
import { Chip } from '@/components/Chip'
import { Button } from '@/components/Button'
import { Field, TextArea } from '@/components/Field'
import { supabase } from '@/lib/supabase'
import type { Need } from '@/lib/types'
import { PACK_COLORS, packForNeedCategory } from '@/design-system/tokens'
import { IconForPack } from '@/design-system/icons'
import { absoluteDateTime, relativeTime } from '@/lib/format'
import { statusColor } from './NeedsListScreen'

export function NeedDetailScreen() {
  const nav = useNavigate()
  const { id } = useParams<{ id: string }>()
  const qc = useQueryClient()

  const [resolutionNote, setResolutionNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const query = useQuery({
    enabled: !!id,
    queryKey: ['need', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('needs').select('*').eq('id', id!).maybeSingle()
      if (error) throw error
      if (!data) throw new Error('Not found')
      return data as Need
    },
  })

  if (query.isLoading) return <Screen title="Need" back><div className="py-10 text-center text-text-tertiary">Loading…</div></Screen>
  if (query.isError) return <Screen title="Need" back><div className="p-5"><ErrorPanel message={(query.error as Error).message} /></div></Screen>

  const need = query.data!
  const pack = packForNeedCategory(need.category)
  const color = PACK_COLORS[pack].light
  const Icon = IconForPack[pack]

  async function resolveNeed() {
    if (!id) return
    setBusy(true); setErr(null)
    try {
      const { error } = await supabase.rpc('resolve_need', {
        p_need_id: id,
        p_resolution_note: resolutionNote.trim() || null,
      })
      if (error) throw new Error(error.message)
      await qc.invalidateQueries({ queryKey: ['needs'] })
      await qc.invalidateQueries({ queryKey: ['need', id] })
      nav(-1)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function setInProgress() {
    if (!id) return
    setBusy(true); setErr(null)
    try {
      const { error } = await supabase.from('needs').update({ status: 'in_progress' }).eq('id', id)
      if (error) throw new Error(error.message)
      await qc.invalidateQueries({ queryKey: ['needs'] })
      await qc.invalidateQueries({ queryKey: ['need', id] })
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Screen title="Need" back>
      <div className="px-5 py-5 space-y-5">
        <div
          className="rounded-r-sm p-5"
          style={{ background: `linear-gradient(180deg, ${color}18, transparent), #141B19`, borderTop: `1px solid ${color}55` }}
        >
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-r-sm flex items-center justify-center shrink-0" style={{ background: color + '22', color }}>
              <Icon size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <Kicker color={color}>{need.category}</Kicker>
              <h2 className="mt-1 font-display text-title text-text-primary">{need.title}</h2>
              {need.description && <p className="mt-2 text-body text-text-secondary">{need.description}</p>}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Chip color={statusColor(need.status)}>{need.status}</Chip>
          <Chip color={need.urgency === 'critical' ? PACK_COLORS.safety.light : color}>{need.urgency}</Chip>
          <Chip color={color}>from {need.source_type}</Chip>
          <ConfidenceChip confidence={need.confidence} />
        </div>

        <section>
          <Kicker>Details</Kicker>
          <div className="mt-2 rounded-r-sm bg-bg-elevated border border-divider-subtle divide-y divide-divider-subtle">
            <Info label="Created" value={`${relativeTime(need.created_at)} · ${absoluteDateTime(need.created_at)}`} />
            {need.due_at && <Info label="Due" value={`${relativeTime(need.due_at)} · ${absoluteDateTime(need.due_at)}`} />}
            {need.resolved_at && <Info label="Resolved" value={`${relativeTime(need.resolved_at)} · ${absoluteDateTime(need.resolved_at)}`} />}
            {need.resolution_note && <Info label="Resolution note" value={need.resolution_note} />}
            {need.blocked_by_need_id && <Info label="Blocked by" value={need.blocked_by_need_id} />}
          </div>
        </section>

        {(need.status === 'open' || need.status === 'in_progress') && (
          <section>
            <Kicker>Resolve</Kicker>
            <div className="mt-2 space-y-3">
              <Field label="Resolution note (optional)">
                <TextArea
                  rows={3}
                  value={resolutionNote}
                  onChange={(e) => setResolutionNote(e.target.value)}
                  placeholder="How was this handled?"
                />
              </Field>
              <div className="flex gap-2">
                {need.status === 'open' && (
                  <Button variant="secondary" size="lg" loading={busy} onClick={setInProgress} className="flex-1">
                    Mark in progress
                  </Button>
                )}
                <Button variant="primary" size="lg" packColor={color} loading={busy} onClick={resolveNeed} className="flex-1">
                  Mark resolved
                </Button>
              </div>
            </div>
          </section>
        )}

        {err && <ErrorPanel message={err} />}
      </div>
    </Screen>
  )
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="px-4 py-2.5">
      <div className="text-caption text-text-tertiary">{label}</div>
      <div className="text-body text-text-primary mt-0.5 break-words">{value}</div>
    </div>
  )
}

function ErrorPanel({ message }: { message: string }) {
  return (
    <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">
      {message}
    </div>
  )
}
